// The interaction layer every chart shares (APS-577; the dataviz rules: a crosshair finds the x,
// one tooltip lists every series, the hit area is bigger than the mark, colour follows the
// series). useChart holds the state; ChartOverlay turns pointer and keys into it; the tooltip,
// the legend's buttons, the tools row and the table read it. Line charts and bar charts use the
// same parts: a bar chart's slot is its "point".
import {
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useReducer,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import { btnClass } from '../d/Controls'
import { Table } from '../d/Table'
import { useChartText } from '../text'
import {
  type ChartState,
  chartReducer,
  hiddenOf,
  INITIAL,
  inSidewaysScroller,
  keyStep,
  nearestIndex,
  type XKey,
  zoomOf,
  zoomWindow,
} from './state'

/** A pick: the shown point's index, and the pointer (viewBox units) when a pointer picked. */
export type Pick_ = (index: number, at: { x: number; y: number } | null) => void

/** A drag shorter than this, in CSS px, is a click, not a zoom. */
const DRAG_PX = 8

export interface UseChartOptions {
  /** Every point's key, in order (the whole series, not the zoomed part). */
  keys: readonly XKey[]
  /** The series' names, in order: the legend hides by index, so other names show every series. */
  names: readonly string[]
  /** Drag-to-zoom: only on a time axis. */
  zoomable: boolean
  /**
   * What the data is, for a chart whose keys are indices (its labels, joined): a zoom set on
   * other data is dropped. Leave it out when the keys last (dates, sample times).
   */
  data?: string | undefined
}

export function useChart({ keys, names, zoomable, data = '' }: UseChartOptions) {
  const [raw, dispatch] = useReducer(chartReducer, INITIAL)
  const [focused, setFocused] = useState(false)
  const [table, setTable] = useState(false)
  // Whether a touch drag zooms: off inside a region that scrolls sideways (ChartFocus checks).
  const [touchZoom, setTouchZoom] = useState(true)
  const series = names.length
  const namesKey = names.join('\u001f')
  // What applies to this data and these series (state.ts hiddenOf, zoomOf).
  const hidden = hiddenOf(raw, namesKey)
  const { zoom, brush } = zoomOf(raw, data)
  const state: ChartState = { ...raw, hidden, zoom, brush }
  const [from, to] = zoomWindow(keys, zoomable ? zoom : null)
  const zoomed = keys.length > 0 && (from > 0 || to < keys.length - 1)
  const viewKeys = keys.slice(from, to + 1)
  const active = raw.active === null ? -1 : viewKeys.indexOf(raw.active)
  const toggle = useCallback(
    (s: number, isolate: boolean) =>
      dispatch({ type: isolate ? 'isolate' : 'toggle', series: s, count: series, names: namesKey }),
    [series, namesKey],
  )
  return {
    state,
    /** The first and last index shown (both included). */
    from,
    to,
    zoomed,
    viewKeys,
    /** The active point's index among the shown ones, or -1. */
    active,
    hidden,
    isHidden: (s: number) => hidden.includes(s),
    toggle,
    point: (key: XKey | null) => dispatch({ type: 'point', key }),
    brushStart: (key: XKey) => dispatch({ type: 'brushStart', key, data }),
    brushTo: (key: XKey) => dispatch({ type: 'brushTo', key }),
    brushEnd: (all: readonly XKey[]) => dispatch({ type: 'brushEnd', keys: all }),
    brushCancel: () => dispatch({ type: 'brushCancel' }),
    reset: () => dispatch({ type: 'reset' }),
    focused,
    setFocused,
    table,
    setTable,
    touchZoom,
    setTouchZoom,
  }
}

/** The touch-action for a chart's hit areas: see ChartOverlay. */
const touchActionOf = (chart: ChartApi, zoomable: boolean) =>
  zoomable && chart.touchZoom ? 'pan-y pinch-zoom' : 'manipulation'

export type ChartApi = ReturnType<typeof useChart>

/**
 * The transparent hit area over the plot, the crosshair (line charts) and the marked span. It
 * snaps to the nearest x, so a reader aims at a date, never at a 2 px line. On a touch screen a
 * sideways drag zooms only when no region around the chart scrolls sideways; a tap shows the
 * tooltip and a second tap on the same point opens it. Pinch-zoom always stays the page's.
 */
export function ChartOverlay({
  chart,
  xs,
  allKeys,
  width,
  left,
  right,
  top,
  bottom,
  crosshair,
  zoomable,
  onPick,
}: {
  chart: ChartApi
  /** The shown points' x positions, in viewBox units, increasing. */
  xs: readonly number[]
  allKeys: readonly XKey[]
  /** The viewBox width. */
  width: number
  left: number
  right: number
  top: number
  bottom: number
  /** A vertical hairline at the active x (line and area charts; bars lift their slot instead). */
  crosshair: boolean
  zoomable: boolean
  /** A click (or Enter) on a shown point; at is the pointer, in viewBox units, when a pointer. */
  onPick?: Pick_ | undefined
}) {
  const { state, viewKeys, active } = chart
  const ref = useRef<SVGRectElement>(null)
  const drag = useRef<{
    clientX: number
    key: XKey
    moved: boolean
    touch: boolean
  } | null>(null)
  const toSvg = (e: PointerEvent<SVGRectElement>) => {
    const svg = e.currentTarget.ownerSVGElement
    const box = svg?.getBoundingClientRect()
    if (!box || box.width === 0) return { x: 0, y: 0 }
    const k = width / box.width
    return { x: (e.clientX - box.left) * k, y: (e.clientY - box.top) * k }
  }
  const keyAt = (x: number) => viewKeys[nearestIndex(xs, x)]
  const zoomsOn = (touch: boolean) => zoomable && (!touch || chart.touchZoom)

  const onPointerDown = (e: PointerEvent<SVGRectElement>) => {
    if (e.button !== 0) return
    const key = keyAt(toSvg(e).x)
    if (key === undefined) return
    const touch = e.pointerType === 'touch'
    drag.current = { clientX: e.clientX, key, moved: false, touch }
    if (zoomsOn(touch)) e.currentTarget.setPointerCapture?.(e.pointerId)
  }
  const onPointerMove = (e: PointerEvent<SVGRectElement>) => {
    const key = keyAt(toSvg(e).x)
    if (key === undefined) return
    const d = drag.current
    // The drag is measured in CSS px, whatever the chart's scale.
    if (d && zoomsOn(d.touch) && Math.abs(e.clientX - d.clientX) >= DRAG_PX) {
      if (!d.moved) chart.brushStart(d.key)
      d.moved = true
      chart.brushTo(key)
    }
    if (e.pointerType !== 'touch' || !d) chart.point(key)
  }
  const onPointerUp = (e: PointerEvent<SVGRectElement>) => {
    const d = drag.current
    drag.current = null
    if (!d) return
    if (d.moved) {
      chart.brushEnd(allKeys)
      return
    }
    const { x, y } = toSvg(e)
    const key = keyAt(x)
    if (key === undefined) return
    const i = viewKeys.indexOf(key)
    // A tap shows the point first; a second tap on it opens it. A click opens it at once.
    if (d.touch && state.active !== key) {
      chart.point(key)
      return
    }
    chart.point(key)
    onPick?.(i, { x, y })
  }
  const onPointerLeave = (e: PointerEvent<SVGRectElement>) => {
    if (e.pointerType === 'touch' || drag.current?.moved) return
    chart.point(null)
  }
  // A page scroll that starts on the chart: the tooltip does not ride along.
  const onPointerCancel = () => {
    drag.current = null
    chart.brushCancel()
    chart.point(null)
  }

  const brush = state.brush ? span(viewKeys, xs, state.brush) : null
  const ax = active >= 0 ? xs[active] : undefined
  return (
    <g>
      {brush ? (
        <rect
          className="ch-brush"
          x={brush[0]}
          y={top}
          width={brush[1] - brush[0]}
          height={bottom - top}
        />
      ) : null}
      {crosshair && ax !== undefined ? (
        <path className="ch-x" d={`M ${ax} ${top} V ${bottom}`} />
      ) : null}
      <rect
        ref={ref}
        className={onPick ? 'ch-hit pick' : 'ch-hit'}
        // A few px past the plot on each side, so the first and last points are easy to reach.
        x={left - 6}
        y={top}
        width={Math.max(0, right - left + 12)}
        height={Math.max(0, bottom - top)}
        // Zooming: a sideways drag is the chart's, scroll and pinch the page's. Otherwise no
        // double-tap zoom, so a tap then a second tap opens the point (Loom, APS-577).
        style={{ touchAction: touchActionOf(chart, zoomable) }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerLeave}
        onPointerCancel={onPointerCancel}
      />
    </g>
  )
}

/** The x positions of a marked span's ends, in order; null when either end is not shown. */
function span(
  keys: readonly XKey[],
  xs: readonly number[],
  brush: NonNullable<ChartState['brush']>,
): readonly [number, number] | null {
  const a = xs[keys.indexOf(brush.from)]
  const b = xs[keys.indexOf(brush.to)]
  if (a === undefined || b === undefined) return null
  return a <= b ? [a, b] : [b, a]
}

/** Whether focus came from the keyboard (or the browser cannot say, as in a test DOM). */
function keyboardFocus(el: Element): boolean {
  try {
    return el.matches(':focus-visible')
  } catch {
    return true
  }
}

/**
 * The keyboard side of a chart, spread on its focus box (ChartFocus): one tab stop; the arrows
 * move the active point (the tooltip shows on keyboard focus), Home and End go to the ends, Shift
 * and an arrow mark a span and Enter zooms to it, Enter opens the point where the chart opens a
 * list, Escape clears the span, then the zoom. A point alone is not Escape's to clear: a dialog
 * around the chart closes on the first press (blur clears the point).
 */
export function chartKeys(
  chart: ChartApi,
  allKeys: readonly XKey[],
  zoomable: boolean,
  onPick?: Pick_ | undefined,
) {
  const { state, viewKeys, active } = chart
  return {
    tabIndex: 0,
    onFocus: (e: FocusEvent<HTMLElement>) => {
      // A mouse click focuses the chart too; only keyboard focus reads points aloud (Loom).
      if (!keyboardFocus(e.currentTarget)) return
      chart.setFocused(true)
      const last = viewKeys.at(-1)
      if (state.active === null && last !== undefined) chart.point(last)
    },
    onBlur: () => {
      chart.setFocused(false)
      chart.point(null)
      chart.brushCancel()
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      const step = keyStep(e.key, active, viewKeys.length)
      if (step !== null) {
        e.preventDefault()
        if (!chart.focused) chart.setFocused(true)
        const key = viewKeys[step] as XKey
        if (e.shiftKey && zoomable) {
          const origin = viewKeys[active < 0 ? step : active] as XKey
          if (!state.brush) chart.brushStart(origin)
          chart.brushTo(key)
        }
        chart.point(key)
        return
      }
      if (e.key === 'Enter') {
        if (state.brush) {
          e.preventDefault()
          chart.brushEnd(allKeys)
        } else if (onPick && active >= 0) {
          e.preventDefault()
          onPick(active, null)
        }
        return
      }
      if (e.key === 'Escape') {
        if (state.brush) chart.brushCancel()
        else if (state.zoom) chart.reset()
        else return
        e.preventDefault()
      }
    },
  }
}

/**
 * The chart's one tab stop, round its <svg>. role application, so a screen reader in browse mode
 * passes the arrows on to it (NVDA and JAWS keep them for the virtual cursor on an image), named
 * a "chart" with the chart's name and its keyboard hint; the <svg> keeps its role img and name.
 */
export function ChartFocus({
  chart,
  label,
  ids,
  allKeys,
  zoomable,
  onPick,
  width,
  children,
}: {
  chart: ChartApi
  label: string
  ids: ReturnType<typeof useChartIds>
  allKeys: readonly XKey[]
  zoomable: boolean
  onPick?: Pick_ | undefined
  /** The chart's width: the scroller check below reruns when it (or the data) changes. */
  width: number
  children: ReactNode
}) {
  const text = useChartText()
  const ref = useRef<HTMLDivElement>(null)
  const { setTouchZoom } = chart
  // Checked again when the chart's width or its data changes: a card that scrolls sideways on a
  // phone may stop doing so on a wider window, or start to once its table loads. touch-action
  // must be set before a touch starts, so it cannot wait for the pointer.
  // biome-ignore lint/correctness/useExhaustiveDependencies: width and the key count are the re-check triggers
  useEffect(() => {
    setTouchZoom(!inSidewaysScroller(ref.current))
  }, [width, allKeys.length, setTouchZoom])
  return (
    <div
      ref={ref}
      // touch-action on an HTML box too: iOS Safari's support on SVG shapes is uncertain (Loom).
      style={{ touchAction: touchActionOf(chart, zoomable) }}
      className="ch-plot"
      role="application"
      aria-roledescription={text.roleName}
      aria-label={label}
      aria-describedby={ids.hint}
      {...chartKeys(chart, allKeys, zoomable, onPick)}
    >
      {children}
    </div>
  )
}

export interface TooltipRow {
  /** The series' index, for its key's step of ink (s0, s1…). */
  series: number
  /** Empty for a chart of one series: the title names it. */
  name: string
  value: string
  /** Bars key with a box, as their legend does; lines with a short stroke. */
  box?: boolean
  /** The key's class instead of `s<series>`, for a chart with its own steps of ink. */
  keyClass?: string
}

/**
 * The tooltip: the point's label, then each shown series' value (strong) and name (muted), keyed
 * by a short stroke in the series' step of ink. Words in text tokens, never the series' colour;
 * the key carries identity. Hidden from screen readers: the live region reads the same words.
 */
export function ChartTooltip({
  x,
  width,
  top,
  title,
  rows,
  hint,
  beside = 0,
}: {
  /** The active x, in viewBox units. */
  x: number
  width: number
  /** From the top of the chart, in px. */
  top: number
  title: string
  rows: readonly TooltipRow[]
  /** What a click opens, as the tooltip's last line. */
  hint?: string | undefined
  /**
   * Half the hovered mark's width (viewBox units): a bar's tooltip opens beside its column, never
   * over the bar and its outline (Mosaic).
   */
  beside?: number
}) {
  // On a phone the halves are too narrow for a name on one line: the tooltip takes the plot's
  // width less 8 px each side, its names on one line (Mosaic). A narrow card on a wide screen
  // with a mouse keeps the tooltip beside the point, off the bars (Mosaic, round 2).
  const phone = useMedia(PHONE)
  if (width < NARROW && phone)
    return (
      <div className="ch-tt wide" style={{ top }} aria-hidden="true" translate="no">
        <TooltipBody title={title} rows={rows} hint={hint} />
      </div>
    )
  const right = x > width / 2
  const edge = right ? x - beside : x + beside
  const at = `${((edge / Math.max(1, width)) * 100).toFixed(3)}%`
  return (
    <div
      className={right ? 'ch-tt end' : 'ch-tt'}
      style={{ left: at, top }}
      aria-hidden="true"
      translate="no"
    >
      <TooltipBody title={title} rows={rows} hint={hint} />
    </div>
  )
}

/** Below this chart width, on a phone, the tooltip spans the plot (ChartTooltip). */
const NARROW = 480
/** A phone: a narrow viewport or a coarse pointer. */
const PHONE = '(max-width: 600px), (pointer: coarse)'

/** Whether a media query matches, following its changes; false where there is no matchMedia. */
function useMedia(query: string): boolean {
  return useSyncExternalStore(
    (change) => {
      const list = typeof matchMedia === 'function' ? matchMedia(query) : null
      list?.addEventListener('change', change)
      return () => list?.removeEventListener('change', change)
    },
    () => (typeof matchMedia === 'function' ? matchMedia(query).matches : false),
    () => false,
  )
}

function TooltipBody({
  title,
  rows,
  hint,
}: {
  title: string
  rows: readonly TooltipRow[]
  hint?: string | undefined
}) {
  return (
    <>
      <div className="ch-tt-h">{title}</div>
      {rows.map((r) => (
        <div className="ch-tt-r" key={r.series}>
          <span className={`ch-key${r.box ? ' box' : ''} ${r.keyClass ?? `s${r.series}`}`} />
          <b>{r.value}</b>
          {r.name ? <span className="ch-tt-n">{r.name}</span> : null}
        </div>
      ))}
      {hint ? <div className="ch-tt-f">{hint}</div> : null}
    </>
  )
}

/** The words a screen reader hears for the active point: the tooltip's, in one line. */
export function readout(title: string, rows: readonly TooltipRow[], hint?: string): string {
  return [
    title,
    ...rows.map((r) => (r.name ? `${r.name} ${r.value}` : r.value)),
    ...(hint ? [hint] : []),
  ].join(', ')
}

/**
 * The legend, as before (a swatch in the series' step of ink, then its name), with each item a
 * button where the chart has several series: a click hides or shows that series, Shift and a
 * click (or its "only" action) shows it alone. The swatch keeps its series' class whatever is
 * hidden, so nothing repaints.
 */
export function ChartLegend({
  names,
  box,
  marker,
  hidden,
  onToggle,
}: {
  names: readonly string[]
  box?: boolean | undefined
  /** The dashed marker's item (a release shipping), after the series (APS-392). */
  marker?: string | undefined
  hidden?: readonly number[] | undefined
  /** Several series: hide, show or isolate one. */
  onToggle?: ((series: number, isolate: boolean) => void) | undefined
}) {
  const interactive = !!onToggle && names.length > 1
  return (
    <ul className="chart-legend" translate="no">
      {names.map((n, i) => {
        const swatch = (
          <span className={box ? `swatch box s${i}` : `swatch s${i}`} aria-hidden="true" />
        )
        return (
          // biome-ignore lint/suspicious/noArrayIndexKey: series keep their order
          <li key={i} className={hidden?.includes(i) ? 'off' : undefined}>
            {interactive ? (
              <LegendToggle
                name={n}
                swatch={swatch}
                shown={!hidden?.includes(i)}
                onToggle={(isolate) => onToggle(i, isolate)}
              />
            ) : (
              <>
                {swatch}
                {n}
              </>
            )}
          </li>
        )
      })}
      {marker ? (
        <li>
          <span className="mk-glyph" aria-hidden="true">
            ┆
          </span>
          {marker}
        </li>
      ) : null}
    </ul>
  )
}

/**
 * A legend item as a button that hides or shows its series (Shift and a click: alone), and its
 * "only" action. ChartLegend uses it; a hand-drawn chart's own legend (Logs, Releases adoption)
 * wraps its swatch and name in it, so its look stays its own.
 */
export function LegendToggle({
  name,
  swatch,
  shown,
  onToggle,
}: {
  name: string
  /** The series' swatch, as the legend draws it. */
  swatch: ReactNode
  shown: boolean
  onToggle: (isolate: boolean) => void
}) {
  const text = useChartText()
  return (
    <>
      <button
        type="button"
        className="lg-b"
        aria-pressed={shown}
        onClick={(e) => onToggle(e.shiftKey || e.altKey || e.metaKey)}
      >
        {swatch}
        {name}
      </button>
      <button
        type="button"
        className="lg-only"
        aria-label={`${text.only}: ${name}`}
        onClick={() => onToggle(true)}
      >
        {text.only}
      </button>
    </>
  )
}

/**
 * The row under a chart: Reset zoom while zoomed, and the Table toggle. The zoomed span is said
 * to screen readers too.
 */
export function ChartTools({
  chart,
  table,
  zoomedFrom,
  zoomedTo,
  tableId,
}: {
  chart: ChartApi
  /** The chart has a table of its own (its toggle shows here). */
  table: boolean
  zoomedFrom: string
  zoomedTo: string
  tableId: string
}) {
  const text = useChartText()
  return (
    <>
      {/* Mounted before it has words, so a screen reader announces the zoom when it fills (Loom).
          A polite live region, not role=status: an empty status on every chart reads as a page
          still loading (e2e expectNoStatus), and status is for that. */}
      <span className="visually-hidden" aria-live="polite" aria-atomic="true" data-zoom-status="">
        {chart.zoomed ? text.zoomed(zoomedFrom, zoomedTo) : ''}
      </span>
      {table || chart.zoomed ? (
        <div className="ch-tools">
          {chart.zoomed ? (
            <button type="button" className={btnClass('ghost', 'sm')} onClick={chart.reset}>
              {text.resetZoom}
            </button>
          ) : null}
          {table ? (
            <button
              type="button"
              className={btnClass('ghost', 'sm')}
              aria-expanded={chart.table}
              aria-controls={tableId}
              onClick={() => chart.setTable(!chart.table)}
            >
              {chart.table ? text.hideTable : text.table}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  )
}

/** The chart's values as a table: what the chart shows (its shown series, its zoomed span). */
export function ChartTable({
  id,
  label,
  xName,
  rows,
  series,
}: {
  id: string
  label: string
  xName?: string | undefined
  rows: readonly { label: string; values: readonly string[] }[]
  series: readonly string[]
}) {
  const text = useChartText()
  return (
    <div id={id} className="ch-table">
      <Table label={label}>
        <thead>
          <tr>
            <th scope="col">{xName ?? text.x}</th>
            {series.map((s, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: series keep their order
              <th key={i} scope="col" className="num" translate="no">
                {s}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional (labels can repeat)
            <tr key={i}>
              <th scope="row" translate="no">
                {r.label}
              </th>
              {r.values.map((v, j) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: columns are positional
                <td key={j} className="num" translate="no">
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

/** Ids for a chart's described-by hint and its table. */
export function useChartIds() {
  const id = useId()
  return { hint: `${id}-k`, table: `${id}-t`, live: `${id}-l` }
}

/** The screen-reader parts: the keyboard hint and the live readout (while focused). */
export function ChartAria({
  ids,
  chart,
  text,
  canOpen,
  canZoom,
}: {
  ids: ReturnType<typeof useChartIds>
  chart: ChartApi
  /** The active point's words. */
  text: string
  canOpen: boolean
  canZoom: boolean
}) {
  const words = useChartText()
  return (
    <>
      <span id={ids.hint} className="visually-hidden">
        {words.keys({ open: canOpen, zoom: canZoom })}
      </span>
      <span id={ids.live} className="visually-hidden" aria-live="polite">
        {chart.focused ? text : ''}
      </span>
    </>
  )
}
